import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import "antd/es/avatar/style";
import _Avatar from "antd/es/avatar";
import _typeof from "@babel/runtime/helpers/esm/typeof";
import _extends from "@babel/runtime/helpers/esm/extends";
import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
var _excluded = ["text", "valueType", "mode", "onChange", "renderFormItem", "value", "readonly"];
import React, { useContext } from 'react';
import { pickProProps, omitUndefined } from '@ant-design/pro-utils';
import ConfigContext, { useIntl } from '@ant-design/pro-provider';
import FieldPercent from './components/Percent';
import FieldIndexColumn from './components/IndexColumn';
import FieldProgress from './components/Progress';
import FieldMoney from './components/Money';
import FieldDatePicker from './components/DatePicker';
import FieldFromNow from './components/FromNow';
import FieldRangePicker from './components/RangePicker';
import FieldCode from './components/Code';
import FieldTimePicker, { FieldTimeRangePicker } from './components/TimePicker';
import FieldText from './components/Text';
import FieldTextArea from './components/TextArea';
import FieldPassword from './components/Password';
import FieldStatus from './components/Status';
import FieldOptions from './components/Options';
import FieldSelect, { proFieldParsingText, proFieldParsingValueEnumToArray } from './components/Select';
import FieldCheckbox from './components/Checkbox';
import FieldRate from './components/Rate';
import FieldSwitch from './components/Switch';
import FieldDigit from './components/Digit';
import FieldSecond from './components/Second';
import FieldRadio from './components/Radio';
import FieldImage from './components/Image';
import FieldCascader from './components/Cascader';
import FieldTreeSelect from './components/TreeSelect';
import FieldColorPicker from './components/ColorPicker';
import FieldDigitRange from './components/DigitRange'; // import type {RangeInputNumberProps,ExtraProps as } from './components/DigitRange'

import { noteOnce } from "rc-util/es/warning";
var REQUEST_VALUE_TYPE = ['select', 'radio', 'radioButton', 'checkbook'];
/**
 * Render valueType object
 *
 * @param text String | number
 * @param valueType ProColumnsValueObjectType
 */

var defaultRenderTextByObject = function defaultRenderTextByObject(text, valueType, props) {
  var pickFormItemProps = pickProProps(props.fieldProps);

  if (valueType.type === 'progress') {
    return /*#__PURE__*/React.createElement(FieldProgress, _extends({}, props, {
      text: text,
      fieldProps: _objectSpread({
        status: valueType.status ? valueType.status : undefined
      }, pickFormItemProps)
    }));
  }

  if (valueType.type === 'money') {
    return /*#__PURE__*/React.createElement(FieldMoney, _extends({
      locale: valueType.locale
    }, props, {
      fieldProps: pickFormItemProps,
      text: text,
      moneySymbol: valueType.moneySymbol
    }));
  }

  if (valueType.type === 'percent') {
    return /*#__PURE__*/React.createElement(FieldPercent, _extends({}, props, {
      text: text,
      showSymbol: valueType.showSymbol,
      precision: valueType.precision,
      fieldProps: pickFormItemProps,
      showColor: valueType.showColor
    }));
  }

  if (valueType.type === 'image') {
    return /*#__PURE__*/React.createElement(FieldImage, _extends({}, props, {
      text: text,
      width: valueType.width
    }));
  }

  return text;
};
/**
 * 根据不同的类型来转化数值
 *
 * @param text
 * @param valueType
 */


var defaultRenderText = function defaultRenderText(text, valueType, props, valueTypeMap) {
  var _props$fieldProps;

  var _props$mode = props.mode,
      mode = _props$mode === void 0 ? 'read' : _props$mode,
      _props$emptyText = props.emptyText,
      emptyText = _props$emptyText === void 0 ? '-' : _props$emptyText;

  if (emptyText !== false && mode === 'read' && valueType !== 'option' && valueType !== 'switch') {
    if (typeof text !== 'boolean' && typeof text !== 'number' && !text) {
      var fieldProps = props.fieldProps,
          render = props.render;

      if (render) {
        return render(text, _objectSpread({
          mode: mode
        }, fieldProps), /*#__PURE__*/React.createElement(React.Fragment, null, emptyText));
      }

      return /*#__PURE__*/React.createElement(React.Fragment, null, emptyText);
    }
  } // eslint-disable-next-line no-param-reassign


  delete props.emptyText;

  if (_typeof(valueType) === 'object') {
    return defaultRenderTextByObject(text, valueType, props);
  }

  var customValueTypeConfig = valueTypeMap && valueTypeMap[valueType];

  if (customValueTypeConfig) {
    // eslint-disable-next-line no-param-reassign
    delete props.ref;

    if (mode === 'read') {
      var _customValueTypeConfi;

      return (_customValueTypeConfi = customValueTypeConfig.render) === null || _customValueTypeConfi === void 0 ? void 0 : _customValueTypeConfi.call(customValueTypeConfig, text, _objectSpread(_objectSpread({
        text: text
      }, props), {}, {
        mode: mode || 'read'
      }), /*#__PURE__*/React.createElement(React.Fragment, null, text));
    }

    if (mode === 'update' || mode === 'edit') {
      var _customValueTypeConfi2;

      return (_customValueTypeConfi2 = customValueTypeConfig.renderFormItem) === null || _customValueTypeConfi2 === void 0 ? void 0 : _customValueTypeConfi2.call(customValueTypeConfig, text, _objectSpread({
        text: text
      }, props), /*#__PURE__*/React.createElement(React.Fragment, null, text));
    }
  }

  var needValueEnum = REQUEST_VALUE_TYPE.includes(valueType);
  var hasValueEnum = !!(props.valueEnum || props.request || props.options || ((_props$fieldProps = props.fieldProps) === null || _props$fieldProps === void 0 ? void 0 : _props$fieldProps.options));
  noteOnce(!needValueEnum || hasValueEnum, "\u5982\u679C\u8BBE\u7F6E\u4E86 valueType \u4E3A ".concat(REQUEST_VALUE_TYPE.join(','), "\u4E2D\u4EFB\u610F\u4E00\u4E2A\uFF0C\u5219\u9700\u8981\u914D\u7F6Eoptions\uFF0Crequest, valueEnum \u5176\u4E2D\u4E4B\u4E00\uFF0C\u5426\u5219\u65E0\u6CD5\u751F\u6210\u9009\u9879\u3002"));
  noteOnce(!needValueEnum || hasValueEnum, "If you set valueType to any of ".concat(REQUEST_VALUE_TYPE.join(','), ", you need to configure options, request or valueEnum."));
  /** 如果是金额的值 */

  if (valueType === 'money') {
    return /*#__PURE__*/React.createElement(FieldMoney, _extends({}, props, {
      text: text
    }));
  }
  /** 如果是日期的值 */


  if (valueType === 'date') {
    return /*#__PURE__*/React.createElement(FieldDatePicker, _extends({
      text: text,
      format: "YYYY-MM-DD"
    }, props));
  }
  /** 如果是周的值 */


  if (valueType === 'dateWeek') {
    return /*#__PURE__*/React.createElement(FieldDatePicker, _extends({
      text: text,
      format: "YYYY-wo",
      picker: "week"
    }, props));
  }
  /** 如果是月的值 */


  if (valueType === 'dateMonth') {
    return /*#__PURE__*/React.createElement(FieldDatePicker, _extends({
      text: text,
      format: "YYYY-MM",
      picker: "month"
    }, props));
  }
  /** 如果是季度的值 */


  if (valueType === 'dateQuarter') {
    return /*#__PURE__*/React.createElement(FieldDatePicker, _extends({
      text: text,
      format: "YYYY-\\QQ",
      picker: "quarter"
    }, props));
  }
  /** 如果是年的值 */


  if (valueType === 'dateYear') {
    return /*#__PURE__*/React.createElement(FieldDatePicker, _extends({
      text: text,
      format: "YYYY",
      picker: "year"
    }, props));
  }
  /** 如果是日期范围的值 */


  if (valueType === 'dateRange') {
    return /*#__PURE__*/React.createElement(FieldRangePicker, _extends({
      text: text,
      format: "YYYY-MM-DD"
    }, props));
  }
  /** 如果是日期加时间类型的值 */


  if (valueType === 'dateTime') {
    return /*#__PURE__*/React.createElement(FieldDatePicker, _extends({
      text: text,
      format: "YYYY-MM-DD HH:mm:ss",
      showTime: true
    }, props));
  }
  /** 如果是日期加时间类型的值的值 */


  if (valueType === 'dateTimeRange') {
    // 值不存在的时候显示 "-"
    return /*#__PURE__*/React.createElement(FieldRangePicker, _extends({
      text: text,
      format: "YYYY-MM-DD HH:mm:ss",
      showTime: true
    }, props));
  }
  /** 如果是时间类型的值 */


  if (valueType === 'time') {
    return /*#__PURE__*/React.createElement(FieldTimePicker, _extends({
      text: text,
      format: "HH:mm:ss"
    }, props));
  }
  /** 如果是时间类型的值 */


  if (valueType === 'timeRange') {
    return /*#__PURE__*/React.createElement(FieldTimeRangePicker, _extends({
      text: text,
      format: "HH:mm:ss"
    }, props));
  }

  if (valueType === 'fromNow') {
    return /*#__PURE__*/React.createElement(FieldFromNow, _extends({
      text: text
    }, props));
  }

  if (valueType === 'index') {
    return /*#__PURE__*/React.createElement(FieldIndexColumn, null, text + 1);
  }

  if (valueType === 'indexBorder') {
    return /*#__PURE__*/React.createElement(FieldIndexColumn, {
      border: true
    }, text + 1);
  }

  if (valueType === 'progress') {
    return /*#__PURE__*/React.createElement(FieldProgress, _extends({}, props, {
      text: text
    }));
  }
  /** 百分比, 默认展示符号, 不展示小数位 */


  if (valueType === 'percent') {
    return /*#__PURE__*/React.createElement(FieldPercent, _extends({
      text: text
    }, props));
  }

  if (valueType === 'avatar' && typeof text === 'string' && props.mode === 'read') {
    return /*#__PURE__*/React.createElement(_Avatar, {
      src: text,
      size: 22,
      shape: "circle"
    });
  }

  if (valueType === 'code') {
    return /*#__PURE__*/React.createElement(FieldCode, _extends({
      text: text
    }, props));
  }

  if (valueType === 'jsonCode') {
    return /*#__PURE__*/React.createElement(FieldCode, _extends({
      text: text,
      language: "json"
    }, props));
  }

  if (valueType === 'textarea') {
    return /*#__PURE__*/React.createElement(FieldTextArea, _extends({
      text: text
    }, props));
  }

  if (valueType === 'digit') {
    return /*#__PURE__*/React.createElement(FieldDigit, _extends({
      text: text
    }, props));
  }

  if (valueType === 'digitRange') {
    return /*#__PURE__*/React.createElement(FieldDigitRange, _extends({
      text: text
    }, props));
  }

  if (valueType === 'second') {
    return /*#__PURE__*/React.createElement(FieldSecond, _extends({
      text: text
    }, props));
  }

  if (valueType === 'select' || valueType === 'text' && (props.valueEnum || props.request)) {
    return /*#__PURE__*/React.createElement(FieldSelect, _extends({
      text: text
    }, props));
  }

  if (valueType === 'checkbox') {
    return /*#__PURE__*/React.createElement(FieldCheckbox, _extends({
      text: text
    }, props));
  }

  if (valueType === 'radio') {
    return /*#__PURE__*/React.createElement(FieldRadio, _extends({
      text: text
    }, props));
  }

  if (valueType === 'radioButton') {
    return /*#__PURE__*/React.createElement(FieldRadio, _extends({
      radioType: "button",
      text: text
    }, props));
  }

  if (valueType === 'rate') {
    return /*#__PURE__*/React.createElement(FieldRate, _extends({
      text: text
    }, props));
  }

  if (valueType === 'switch') {
    return /*#__PURE__*/React.createElement(FieldSwitch, _extends({
      text: text
    }, props));
  }

  if (valueType === 'option') {
    return /*#__PURE__*/React.createElement(FieldOptions, _extends({
      text: text
    }, props));
  }

  if (valueType === 'password') {
    return /*#__PURE__*/React.createElement(FieldPassword, _extends({
      text: text
    }, props));
  }

  if (valueType === 'image') {
    return /*#__PURE__*/React.createElement(FieldImage, _extends({
      text: text
    }, props));
  }

  if (valueType === 'cascader') {
    return /*#__PURE__*/React.createElement(FieldCascader, _extends({
      text: text
    }, props));
  }

  if (valueType === 'treeSelect') {
    return /*#__PURE__*/React.createElement(FieldTreeSelect, _extends({
      text: text
    }, props));
  }

  if (valueType === 'color') {
    return /*#__PURE__*/React.createElement(FieldColorPicker, _extends({
      text: text
    }, props));
  }

  return /*#__PURE__*/React.createElement(FieldText, _extends({
    text: text
  }, props));
};

export { defaultRenderText };

var ProField = function ProField(_ref, ref) {
  var _ref2, _fieldProps$value, _ref3;

  var text = _ref.text,
      _ref$valueType = _ref.valueType,
      valueType = _ref$valueType === void 0 ? 'text' : _ref$valueType,
      _ref$mode = _ref.mode,
      mode = _ref$mode === void 0 ? 'read' : _ref$mode,
      _onChange = _ref.onChange,
      renderFormItem = _ref.renderFormItem,
      value = _ref.value,
      readonly = _ref.readonly,
      rest = _objectWithoutProperties(_ref, _excluded);

  var intl = useIntl();
  var context = useContext(ConfigContext);

  var fieldProps = (value !== undefined || _onChange || (rest === null || rest === void 0 ? void 0 : rest.fieldProps)) && _objectSpread(_objectSpread({
    value: value
  }, omitUndefined(rest === null || rest === void 0 ? void 0 : rest.fieldProps)), {}, {
    onChange: function onChange() {
      var _rest$fieldProps, _rest$fieldProps$onCh;

      for (var _len = arguments.length, restParams = new Array(_len), _key = 0; _key < _len; _key++) {
        restParams[_key] = arguments[_key];
      }

      rest === null || rest === void 0 ? void 0 : (_rest$fieldProps = rest.fieldProps) === null || _rest$fieldProps === void 0 ? void 0 : (_rest$fieldProps$onCh = _rest$fieldProps.onChange) === null || _rest$fieldProps$onCh === void 0 ? void 0 : _rest$fieldProps$onCh.call.apply(_rest$fieldProps$onCh, [_rest$fieldProps].concat(restParams));
      _onChange === null || _onChange === void 0 ? void 0 : _onChange.apply(void 0, restParams);
    }
  });

  return /*#__PURE__*/React.createElement(React.Fragment, null, defaultRenderText(mode === 'edit' ? (_ref2 = (_fieldProps$value = fieldProps === null || fieldProps === void 0 ? void 0 : fieldProps.value) !== null && _fieldProps$value !== void 0 ? _fieldProps$value : text) !== null && _ref2 !== void 0 ? _ref2 : '' : (_ref3 = text !== null && text !== void 0 ? text : fieldProps === null || fieldProps === void 0 ? void 0 : fieldProps.value) !== null && _ref3 !== void 0 ? _ref3 : '', valueType || 'text', _objectSpread(_objectSpread({
    ref: ref
  }, rest), {}, {
    mode: readonly ? 'read' : mode,
    renderFormItem: renderFormItem ? function () {
      var newDom = renderFormItem.apply(void 0, arguments); // renderFormItem 之后的dom可能没有props，这里会帮忙注入一下

      if ( /*#__PURE__*/React.isValidElement(newDom)) return /*#__PURE__*/React.cloneElement(newDom, _objectSpread(_objectSpread({
        placeholder: rest.placeholder || intl.getMessage('tableForm.inputPlaceholder', '请输入')
      }, fieldProps), newDom.props || {}));
      return newDom;
    } : undefined,
    placeholder: rest.placeholder || intl.getMessage('tableForm.inputPlaceholder', '请输入'),
    fieldProps: pickProProps(fieldProps)
  }), context.valueTypeMap));
};

export { FieldPercent, FieldIndexColumn, FieldProgress, FieldMoney, FieldDatePicker, FieldRangePicker, FieldCode, FieldTimePicker, FieldText, FieldStatus, FieldSelect, proFieldParsingText, proFieldParsingValueEnumToArray };
export default /*#__PURE__*/React.forwardRef(ProField);